import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Checklist from '../Checklist';
import { FormContainer, Wrapper } from './styles';
import { Columns, Select, TextArea, Input, Tooltip, Separator, Checkbox, Card, Button } from '@myob/myob-widgets';
import reasonList from './reasonList';
import { useForm, Form as FormType, FormValidationType, Field, RunTime } from 'hooks/useForm';
import { AppThunkDispatch, RootState } from 'stores';
import { CancellationState } from 'modules/Cancellation/reducers/type';
import { ButtonWrapper } from 'modules/Cancellation/styles';
import { formattedSerialNumberString } from 'modules/Cancellation/helper';
import { sendFormData } from 'modules/Cancellation/reducers';
import { Action } from 'redux';
import { useHistory } from 'react-router-dom';
import { ProductCancellationState } from '../../reducers/type';

export type Props = {
    isProductCancellation: boolean;
};

export enum CancellationFormFields {
    CONTACT_NUMBER = 'contactNumber',
    REASON = 'reason',
    COMMENTS = 'additionalComments',
    SERIAL_NUMBER = 'serialNumber',
    TERMS = 'terms',
}

export type CancellationForm = {
    [CancellationFormFields.CONTACT_NUMBER]: string;
    [CancellationFormFields.SERIAL_NUMBER]: string;
    [CancellationFormFields.TERMS]: boolean;
    [CancellationFormFields.COMMENTS]: string;
    [CancellationFormFields.REASON]: string;
};

export const Form: React.FC<Props> = ({ isProductCancellation }) => {
    const defaultForm: FormType = {
        [CancellationFormFields.CONTACT_NUMBER]: {
            rules: [
                {
                    type: FormValidationType.Required,
                    errorMessage: 'Please enter a valid phone number',
                },
            ],
            customRules: [
                {
                    errorMessage: 'Please enter a valid phone number',
                    runTime: [RunTime.OnBlur],
                    regExp: /^\+?[00]?0?(61)?0?[234578]\d{8}$|(^\+?[00]?0?(?:6(?!1))?4?0?)((?:2(?:\d{9}|\d{8}|\d{7}))|(?:[34679](?:\d{7})))$/,
                },
                {
                    errorMessage: 'Please enter a valid phone number',
                    runTime: [RunTime.OnChange],
                    regExp: /[^0-9\+]/g,
                },
            ],
            violatedRules: [],
            value: '',
            isValid: false,
        },
        [CancellationFormFields.SERIAL_NUMBER]: {
            value: '',
            rules: [
                {
                    type: FormValidationType.Required,
                    errorMessage: 'Please enter your product serial number',
                },
                {
                    type: FormValidationType.NumberOnly,
                    errorMessage: 'Please enter a valid product serial number',
                },
            ],
            violatedRules: [],
            isValid: false,
        },
        [CancellationFormFields.REASON]: {
            value: '',
            rules: [{ type: FormValidationType.Required, errorMessage: '' }],
            violatedRules: [],
            isValid: false,
        },
        [CancellationFormFields.COMMENTS]: {
            value: '',
            violatedRules: [],
            isValid: false,
        },
        [CancellationFormFields.TERMS]: {
            value: false,
            violatedRules: [],
            isValid: false,
            rules: [
                {
                    type: FormValidationType.Required,
                    errorMessage: 'Please confirm you’ve acknowledged the below conditions',
                },
            ],
        },
    };
    const { form, onBlur, onChange, onKeyDown, setForm, getErrorMessage, isFormValid, getValue, resetForm } = useForm(
        defaultForm,
    );
    const [commentsTitle, setCommentsTitle] = useState('Additional comments');

    const { initialSerialNumber: decryptSerialNumber, productName, isSubmitting } = useSelector<
        RootState,
        CancellationState
    >(state => state.cancellation);

    const history = useHistory<ProductCancellationState>();

    const locationState = history.location.state;
    const serialNumber = locationState?.serialNumber || decryptSerialNumber;

    const serialNumberWithProductName = formattedSerialNumberString(
        serialNumber,
        locationState?.productName || productName,
    );

    const [isSNDisabled, setIsSNDisabled] = useState(!!serialNumberWithProductName);

    const dispatch: AppThunkDispatch = useDispatch();
    const updateSNRule = useCallback(() => {
        if (isSNDisabled) {
            setForm(pre => ({
                ...pre,
                [CancellationFormFields.SERIAL_NUMBER]: {
                    ...pre[CancellationFormFields.SERIAL_NUMBER],
                    rules: [],
                    value: serialNumberWithProductName,
                },
            }));
        }
    }, [serialNumberWithProductName, setForm, isSNDisabled]);

    useEffect(() => {
        updateSNRule();
    }, [updateSNRule]);

    const onSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e);
        const comments: Field = form[CancellationFormFields.COMMENTS];
        if (e.currentTarget.value === 'other') {
            comments.rules = [];
            comments.rules.push({
                type: FormValidationType.Required,
                errorMessage: 'Please provide more information on your cancellation reason',
            });
            setCommentsTitle('Additional comments*');
        } else {
            comments.rules = [];
            comments.violatedRules = [];
            setCommentsTitle('Additional comments');
        }
        setForm(pre => ({ ...pre, [CancellationFormFields.COMMENTS]: comments }));
    };
    const onClickSubmitCancellation = () => {
        const formValue = getValue();
        if (isSNDisabled) {
            formValue[CancellationFormFields.SERIAL_NUMBER] = serialNumber;
        }
        dispatch(sendFormData(formValue as CancellationForm)).then((res: Action) => {
            if (res.type === sendFormData.fulfilled.type) {
                resetForm();
                setIsSNDisabled(false);
                history.replace(history.location.pathname);
            }
        });
    };

    return (
        <>
            <Card>
                <Wrapper>
                    <FormContainer>
                        <h2>Your cancellation details</h2>
                        <Columns className="columns">
                            <Select
                                data-testid="select"
                                name={CancellationFormFields.REASON}
                                label="Reason for cancellation*"
                                onChange={onSelectChange}
                                value={form[CancellationFormFields.REASON].value}
                            >
                                {reasonList.map(reason => (
                                    <Select.Option
                                        key={reason.label}
                                        value={reason.value}
                                        label={reason.label}
                                        disabled={reason.disabled}
                                    />
                                ))}
                            </Select>
                        </Columns>
                        <Columns className="columns">
                            <Input
                                name={CancellationFormFields.CONTACT_NUMBER}
                                label="Your preferred contact number*"
                                labelAccessory={
                                    <Tooltip className="contact-number-tooltip" trigger="click">
                                        This number must be for the person currently logged in
                                    </Tooltip>
                                }
                                onChange={onChange}
                                onBlur={onBlur}
                                onKeyPress={onKeyDown}
                                value={form[CancellationFormFields.CONTACT_NUMBER].value}
                                errorMessage={getErrorMessage(CancellationFormFields.CONTACT_NUMBER)}
                            />
                            <Input
                                name={CancellationFormFields.SERIAL_NUMBER}
                                label="Serial number*"
                                disabled={isSNDisabled}
                                onChange={onChange}
                                onBlur={onBlur}
                                onKeyPress={onKeyDown}
                                errorMessage={getErrorMessage(CancellationFormFields.SERIAL_NUMBER)}
                                maxLength={12}
                                value={form[CancellationFormFields.SERIAL_NUMBER].value}
                                labelAccessory={
                                    <Tooltip className="serial-number-tooltip" trigger="click">
                                        {isProductCancellation
                                            ? "You can find your serial number under 'Manage my product' on the previous page"
                                            : 'You can find your serial number next to the product name in my.myob'}
                                    </Tooltip>
                                }
                            />
                        </Columns>
                        <TextArea
                            name={CancellationFormFields.COMMENTS}
                            label={commentsTitle}
                            rows={8}
                            value={form[CancellationFormFields.COMMENTS].value}
                            resize="none"
                            errorMessage={getErrorMessage(CancellationFormFields.COMMENTS)}
                            onBlur={onBlur}
                            onChange={onChange}
                            maxLength={300}
                        />
                        <Separator />
                        <div className="checkBoxWrapper">
                            <div data-testid="terms">
                                <Checkbox
                                    name={CancellationFormFields.TERMS}
                                    label="I acknowledge that:"
                                    checked={form[CancellationFormFields.TERMS].value}
                                    onChange={onChange}
                                    errorMessage={getErrorMessage('terms')}
                                />
                            </div>
                            <ul className="termsList">
                                <li>
                                    I will receive a phone call on my preferred contact number within the next 5
                                    business days in regards to this request
                                </li>
                                <li>Cancellation isn&apos;t finalised until I receive a call back from MYOB</li>
                            </ul>
                        </div>
                    </FormContainer>
                    <Checklist />
                </Wrapper>
            </Card>
            <ButtonWrapper>
                <Button
                    name="submit_btn"
                    type="primary"
                    onClick={onClickSubmitCancellation}
                    disabled={!isFormValid || isSubmitting}
                >
                    Submit call back request
                </Button>
            </ButtonWrapper>
        </>
    );
};

export default Form;
